import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { buildPreviousRange } from '@/lib/campaign-dashboard'
import { fetchCampaignDashboard } from '@/lib/campaign-reports'
import type { CampaignDashboardPayload } from '@/types/campaign-report'

interface UseCampaignDashboardOptions {
  campaignId: number | null
  dateFrom: string
  dateTo: string
  enabled?: boolean
}

export function useCampaignDashboard({
  campaignId,
  dateFrom,
  dateTo,
  enabled = true,
}: UseCampaignDashboardOptions) {
  const { token } = useAuth()
  const [data, setData] = useState<CampaignDashboardPayload | null>(null)
  const [previousData, setPreviousData] = useState<CampaignDashboardPayload | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const previousRange = useMemo(
    () => buildPreviousRange({ dateFrom, dateTo }),
    [dateFrom, dateTo]
  )

  const load = useCallback(async () => {
    if (!enabled) {
      setData(null)
      setPreviousData(null)
      setIsLoading(false)
      setError(null)
      return
    }

    if (!campaignId) {
      setData(null)
      setPreviousData(null)
      setIsLoading(false)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)

    const [currentResult, previousResult] = await Promise.allSettled([
      fetchCampaignDashboard(campaignId, { dateFrom, dateTo }, token),
      fetchCampaignDashboard(
        campaignId,
        {
          dateFrom: previousRange.dateFrom,
          dateTo: previousRange.dateTo,
        },
        token
      ),
    ])

    if (currentResult.status === 'fulfilled') {
      setData(currentResult.value)
    } else {
      setData(null)
      setError(
        currentResult.reason instanceof Error
          ? currentResult.reason.message
          : 'Nao foi possivel carregar o dashboard da campanha.'
      )
    }

    if (previousResult.status === 'fulfilled') {
      setPreviousData(previousResult.value)
    } else {
      setPreviousData(null)
    }

    setIsLoading(false)
  }, [campaignId, dateFrom, dateTo, enabled, previousRange.dateFrom, previousRange.dateTo, token])

  useEffect(() => {
    const runLoad = window.setTimeout(() => {
      void load()
    }, 0)

    return () => window.clearTimeout(runLoad)
  }, [load])

  return {
    data,
    previousData,
    previousRange,
    isLoading,
    error,
    refetch: load,
  }
}
